import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Loader2, Download, Wifi, WifiOff, ClipboardList as ClipboardListIcon, Eye, Trash2, Send, RefreshCw, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import InstallPrompt from './InstallPrompt';
import {
  downloadCountPackage,
  getAllCountPackages,
  removeCountPackage,
  COUNT_SYNC_STATES,
} from '../../services/countOfflineService';

export default function CountSessionPickerPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [localPackages, setLocalPackages] = useState({});
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Online/offline detection
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Load local packages from IndexedDB — defensive try-catch
  const loadLocalPackages = useCallback(async () => {
    try {
      const pkgs = await getAllCountPackages();
      const map = {};
      for (const p of pkgs) {
        map[p.id] = p;
      }
      setLocalPackages(map);
    } catch (e) {
      console.error('Failed to load local packages from IndexedDB:', e);
      // Don't crash — just show no local packages
      setLocalPackages({});
    }
  }, []);

  // Fetch assigned sessions from API
  const fetchSessions = useCallback(async () => {
    if (!isOnline) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/count/sessions');
      setSessions(res.data.items || []);
    } catch (e) {
      console.error('Failed to load sessions:', e);
    } finally {
      setLoading(false);
    }
  }, [isOnline]);

  useEffect(() => {
    loadLocalPackages();
    fetchSessions();
  }, [loadLocalPackages, fetchSessions]);

  // Download and enter count mode
  const handleDownload = async (sessionId) => {
    setDownloading(sessionId);
    const result = await downloadCountPackage(sessionId);
    setDownloading(null);
    if (result.success) {
      await loadLocalPackages();
      navigate(`/count/sessions/${sessionId}`);
    }
  };

  // Resume from local
  const handleResume = (sessionId) => {
    navigate(`/count/sessions/${sessionId}`);
  };

  // Remove/delete local package
  const handleRemove = async (sessionId) => {
    if (!window.confirm('Delete this session from device? You can re-download it later.')) return;
    await removeCountPackage(sessionId);
    await loadLocalPackages();
  };

  // Re-download: replace local data with fresh server data
  const handleRedownload = async (sessionId) => {
    if (!window.confirm(
      'Re-download will REPLACE all local count data for this session with the latest from the server.\n\n' +
      'Any unsynced local counts will be lost.\n\nProceed?'
    )) return;
    setDownloading(sessionId);
    await removeCountPackage(sessionId);
    const result = await downloadCountPackage(sessionId);
    setDownloading(null);
    if (result.success) {
      await loadLocalPackages();
      navigate(`/count/sessions/${sessionId}`);
    }
  };

  // Detect finalized sessions: local packages not on server anymore (while online)
  const [finalisedSessions, setFinalisedSessions] = useState([]);
  useEffect(() => {
    if (!isOnline || sessions.length === 0 || Object.keys(localPackages).length === 0) return;
    const serverIds = new Set(sessions.map(s => s.id));
    const finalised = [];
    for (const [id, pkg] of Object.entries(localPackages)) {
      const numId = parseInt(id, 10);
      if (!serverIds.has(numId)) {
        finalised.push({ id: numId, site_name: pkg.site_name, session_reference: pkg.session_reference, sync_state: pkg.sync_state });
      }
    }
    setFinalisedSessions(finalised);
  }, [isOnline, sessions, localPackages]);

  const handleDismissFinalised = async (sessionId) => {
    await removeCountPackage(sessionId);
    setFinalisedSessions(prev => prev.filter(s => s.id !== sessionId));
    await loadLocalPackages();
    toast.info('Session offloaded from device');
  };

  const handleDismissAllFinalised = async () => {
    for (const s of finalisedSessions) {
      await removeCountPackage(s.id);
    }
    setFinalisedSessions([]);
    await loadLocalPackages();
    toast.info('All finalised sessions offloaded');
  };

  // Merge server sessions + local-only packages
  // When online: server response is authoritative — only show local packages that also appear on the server
  // When offline: show all local packages except parent multi-location sessions (not for counting)
  const mergedSessions = [...sessions];
  const serverIds = new Set(sessions.map(s => s.id));
  for (const [id, pkg] of Object.entries(localPackages)) {
    const numId = parseInt(id, 10);
    if (!serverIds.has(numId)) {
      // If online, the server excluded this session (finalised/locked) — handled by finalisedSessions
      if (isOnline && sessions.length > 0) continue;

      // Offline fallback: still filter out parent multi-location sessions from local cache
      const pkgSession = pkg.package?.session;
      const auditMode = pkgSession?.audit_mode || 'single';
      const parentId = pkgSession?.parent_session_id;
      if (auditMode !== 'single' && !parentId) {
        continue; // Parent session — skip
      }
      mergedSessions.push({
        id: numId,
        session_reference: pkg.session_reference || `Session ${id}`,
        site_name: pkg.site_name || 'Unknown site',
        session_date: pkg.session_date,
        status: pkgSession?.status || 'in_progress',
        line_count: pkg.package?.count_sheet_lines?.length || 0,
        counted_lines: Object.keys(pkg.local_counts || {}).length,
        audit_location_name: pkgSession?.audit_location_name || null,
        _localOnly: true,
      });
    }
  }

  const syncStateBadge = (syncState) => {
    const map = {
      [COUNT_SYNC_STATES.DOWNLOADED]: { label: 'Downloaded', cls: 'bg-slate-50 text-slate-700 border-slate-200' },
      [COUNT_SYNC_STATES.COUNTING]: { label: 'Counting', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
      [COUNT_SYNC_STATES.READY_FOR_TRANSFER]: { label: 'Ready for Transfer', cls: 'bg-purple-50 text-purple-700 border-purple-200' },
      [COUNT_SYNC_STATES.SYNCING]: { label: 'Syncing...', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
      [COUNT_SYNC_STATES.SYNCED]: { label: 'Synced', cls: 'bg-green-50 text-green-700 border-green-200' },
      [COUNT_SYNC_STATES.IMPORTED]: { label: 'Imported', cls: 'bg-teal-50 text-teal-700 border-teal-200' },
      [COUNT_SYNC_STATES.PARTIAL]: { label: 'Partial Sync', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
      [COUNT_SYNC_STATES.CONFLICT]: { label: 'Conflict', cls: 'bg-red-50 text-red-700 border-red-200' },
      [COUNT_SYNC_STATES.ERROR]: { label: 'Error', cls: 'bg-red-50 text-red-700 border-red-200' },
    };
    const info = map[syncState] || { label: syncState || 'Ready', cls: 'bg-slate-50 text-slate-600 border-slate-200' };
    return <Badge variant="outline" className={`text-[9px] ${info.cls}`}>{info.label}</Badge>;
  };

  // Determine user role context
  const userRoles = (user?.roles || []).map(r => r.toLowerCase());
  const legacyRole = (user?.role || '').toLowerCase();
  const allRoles = new Set([...userRoles, legacyRole].filter(Boolean));
  const isAuditor = allRoles.has('auditor');
  const isManagement = allRoles.has('admin') || allRoles.has('scheduler') || allRoles.has('senior_scheduler');

  return (
    <div className="p-4 max-w-2xl mx-auto" data-testid="count-session-picker">
      {/* Install Prompt */}
      <InstallPrompt />

      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900" data-testid="count-picker-title">
            AbbeyZone Count
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Welcome, {user?.first_name}
          </p>
        </div>
        <div className={`flex items-center gap-1.5 text-sm ${isOnline ? 'text-green-600' : 'text-amber-600'}`} data-testid="count-online-status">
          {isOnline ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
          {isOnline ? 'Online' : 'Offline'}
        </div>
      </div>

      {/* Role context banner for management users */}
      {isManagement && !isAuditor && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-slate-100 border border-slate-200 text-sm text-slate-600" data-testid="count-management-banner">
          <Eye className="w-4 h-4 shrink-0" />
          <span>Viewing as <strong>{legacyRole || 'manager'}</strong> — all site sessions shown for oversight</span>
        </div>
      )}

      {/* Finalised sessions banner — inform user and offload */}
      {finalisedSessions.length > 0 && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3" data-testid="finalised-sessions-banner">
          <div className="flex items-start gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-800">
                {finalisedSessions.length === 1 ? 'Session Finalised' : `${finalisedSessions.length} Sessions Finalised`}
              </p>
              <p className="text-xs text-green-700 mt-0.5">
                {finalisedSessions.length === 1
                  ? 'This session has been published on the server. Count data can be safely removed from this device.'
                  : 'These sessions have been published on the server. Count data can be safely removed from this device.'}
              </p>
            </div>
          </div>
          <div className="space-y-1.5 ml-7">
            {finalisedSessions.map(s => (
              <div key={s.id} className="flex items-center justify-between text-xs bg-white/60 rounded px-2.5 py-1.5 border border-green-100">
                <div>
                  <span className="font-medium text-green-900">{s.site_name}</span>
                  <span className="text-green-600 ml-2">{s.session_reference}</span>
                  {s.sync_state === 'synced' && <Badge variant="outline" className="ml-2 text-[9px] bg-green-100 text-green-700 border-green-200 px-1 py-0">Synced</Badge>}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs text-green-700 hover:text-green-900 hover:bg-green-100 px-2"
                  onClick={() => handleDismissFinalised(s.id)}
                  data-testid={`dismiss-finalised-${s.id}`}
                >
                  Offload
                </Button>
              </div>
            ))}
          </div>
          {finalisedSessions.length > 1 && (
            <Button
              size="sm"
              variant="outline"
              className="mt-2 ml-7 text-xs h-7 border-green-300 text-green-700 hover:bg-green-100"
              onClick={handleDismissAllFinalised}
              data-testid="dismiss-all-finalised"
            >
              Offload All
            </Button>
          )}
        </div>
      )}

      {/* Info line */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">
          {isOnline ? 'Download sessions to count offline on this device' : 'Showing sessions on this device'}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : mergedSessions.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="count-no-sessions">
          <ClipboardListIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
          {isOnline ? (
            <>
              <p className="text-base">No sessions assigned to you</p>
              <p className="text-sm mt-1">Ask your scheduler to assign sessions.</p>
            </>
          ) : (
            <>
              <p className="text-base">No sessions on device</p>
              <p className="text-sm mt-1">Go online to browse and download sessions.</p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {mergedSessions.map(session => {
            const localPkg = localPackages[session.id];
            const hasLocal = !!localPkg;
            const countedLocal = hasLocal ? Object.keys(localPkg.local_counts || {}).length : session.counted_lines || 0;

            return (
              <Card
                key={session.id}
                className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                data-testid={`count-session-card-${session.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-semibold text-slate-900 truncate">{session.site_name}</span>
                      {session.audit_location_name && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-slate-300 text-slate-600 bg-slate-50 shrink-0">{session.audit_location_name}</Badge>
                      )}
                      {session.session_type === 'valuation' ? (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-purple-200 text-purple-700 bg-purple-50 shrink-0">Valuation</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-blue-200 text-blue-700 bg-blue-50 shrink-0">Stock Audit</Badge>
                      )}
                      {hasLocal && syncStateBadge(localPkg.sync_state)}
                    </div>
                    <p className="text-sm text-slate-500">{session.session_reference}</p>
                    <p className="text-sm text-slate-400 mt-1">
                      {session.session_date} &middot; {countedLocal}/{session.line_count} counted
                    </p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {hasLocal ? (
                      <>
                        <Button
                          size="lg"
                          onClick={() => handleResume(session.id)}
                          className="min-h-[44px]"
                          data-testid={`count-resume-btn-${session.id}`}
                        >
                          Resume
                        </Button>
                        {isOnline && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRedownload(session.id); }}
                            className="p-2 text-slate-300 hover:text-amber-600 transition-colors"
                            title="Re-download from server (replaces local data)"
                            data-testid={`count-redownload-btn-${session.id}`}
                          >
                            <RefreshCw className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRemove(session.id); }}
                          className="p-2 text-slate-300 hover:text-red-500 transition-colors"
                          title="Delete from device"
                          data-testid={`count-remove-btn-${session.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <Button
                        size="lg"
                        onClick={() => handleDownload(session.id)}
                        disabled={!isOnline || downloading === session.id}
                        className="min-h-[44px]"
                        data-testid={`count-download-btn-${session.id}`}
                      >
                        {downloading === session.id ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        Download to Device
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Actions — transfer */}
      <div className="mt-6 border-t pt-4 space-y-2">
        <Button
          variant="outline"
          className="w-full gap-2 text-sm"
          onClick={() => navigate('/count/transfer')}
          data-testid="transfer-nav-btn"
        >
          <Send className="h-4 w-4" />
          Transfer Count Data
          <span className="text-[10px] text-muted-foreground ml-auto">Send or receive counts between devices</span>
        </Button>
      </div>
    </div>
  );
}
